import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Radio, Lock, Eye, EyeOff, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState("");
  const [success, setSuccess] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState("");

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setTokenError("No se proporcionó un token válido");
        setVerifying(false);
        return;
      }

      try {
        const response = await axios.get(`${API_BASE}/auth/verify-reset-token/${token}`);
        setTokenValid(true);
        setMaskedEmail(response.data.email || "");
      } catch (error) {
        setTokenError(
          error.response?.data?.detail || 
          "El enlace es inválido o ha expirado"
        );
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Las contraseñas no coinciden");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_BASE}/auth/reset-password`, {
        token,
        new_password: password,
      });
      setSuccess(true);
      toast.success("Contraseña actualizada exitosamente");
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (error) {
      toast.error(
        error.response?.data?.detail || 
        "Error al restablecer la contraseña"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-[#34C759]/10 mb-4">
            <Radio className="w-8 h-8 text-[#34C759]" />
          </div>
          <h1 className="text-2xl font-heading font-bold text-[#F4F4F5]">
            AVX RF PRO
          </h1>
          <p className="text-sm text-[#71717A]">ANLEON CHILE</p>
        </div>

        {/* Card */}
        <div className="bg-[#0A0A0A] border border-white/[0.08] rounded-lg p-6">
          {verifying ? (
            /* Verifying State */
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 text-[#34C759] animate-spin mx-auto mb-4" />
              <p className="text-[#71717A]">Verificando enlace...</p>
            </div>
          ) : tokenError ? (
            /* Error State */
            <div className="text-center py-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#FF3B30]/10 mb-4">
                <XCircle className="w-8 h-8 text-[#FF3B30]" />
              </div>
              <h2 className="text-xl font-heading font-semibold text-[#F4F4F5] mb-2">
                Enlace inválido
              </h2>
              <p className="text-sm text-[#71717A] mb-6">
                {tokenError}
              </p>
              <Link to="/forgot-password">
                <Button className="btn-primary">
                  Solicitar nuevo enlace
                </Button>
              </Link>
            </div>
          ) : success ? (
            /* Success State */
            <div className="text-center py-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#34C759]/10 mb-4">
                <CheckCircle2 className="w-8 h-8 text-[#34C759]" />
              </div>
              <h2 className="text-xl font-heading font-semibold text-[#F4F4F5] mb-2">
                ¡Contraseña actualizada!
              </h2>
              <p className="text-sm text-[#71717A] mb-4">
                Tu contraseña ha sido restablecida exitosamente.
              </p>
              <p className="text-xs text-[#71717A]">
                Serás redirigido al inicio de sesión...
              </p>
            </div>
          ) : (
            /* Reset Form */
            <>
              <div className="text-center mb-6">
                <h2 className="text-xl font-heading font-semibold text-[#F4F4F5] mb-2">
                  Nueva contraseña
                </h2>
                <p className="text-sm text-[#71717A]">
                  Ingresa tu nueva contraseña para 
                  {maskedEmail && <span className="text-[#F4F4F5]"> {maskedEmail}</span>}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="form-group">
                  <Label htmlFor="password" className="form-label">
                    Nueva contraseña
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="input-tactical pl-10 pr-10"
                      placeholder="Mínimo 6 caracteres"
                      autoComplete="new-password"
                      data-testid="reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-[#A1A1AA]"
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <Label htmlFor="confirmPassword" className="form-label">
                    Confirmar contraseña
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
                    <Input
                      id="confirmPassword"
                      type={showPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="input-tactical pl-10"
                      placeholder="Repite la contraseña"
                      autoComplete="new-password"
                      data-testid="reset-confirm-input"
                    />
                  </div>
                  {confirmPassword && password !== confirmPassword && (
                    <p className="text-xs text-[#FF3B30] mt-1">
                      Las contraseñas no coinciden
                    </p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full btn-primary"
                  disabled={loading || password.length < 6 || password !== confirmPassword}
                  data-testid="reset-submit-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    "Restablecer contraseña"
                  )}
                </Button>
              </form>
            </>
          )}

          {/* Back to login (only show when not success) */}
          {!success && (
            <div className="mt-6 pt-4 border-t border-white/[0.08] text-center">
              <Link
                to="/login"
                className="text-sm text-[#71717A] hover:text-[#A1A1AA] transition-colors"
              >
                Volver al inicio de sesión
              </Link>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-[#71717A] mt-6">
          © 2026 AVX RF PRO - Anleon Chile
        </p>
      </div>
    </div>
  );
}
